/* global globalThis */
import { useContext, useEffect, useRef, useState } from 'react';
import { useCountryConfig } from 'hooks/useCountryConfig';
import { Context } from 'context';
import zendeskCustomLauncher from 'assets/images/zendesk-custom-launcher.png';
import {
  applyMetadata,
  getZendesk,
  syncPortal,
  waitForZendesk,
} from 'utils/zendesk-messenger-tags';
import './zendesk-widget.scss';

const ZE_SNIPPET_ID = 'ze-snippet';
const ZENDESK_SNIPPET_BASE_URL = 'https://static.zdassets.com/ekr/snippet.js';
const ZENDESK_MESSENGER = 'messenger';
const ZENDESK_MESSENGER_ON = 'messenger:on';
const ZENDESK_ACTION_HIDE = 'hide';
const ZENDESK_ACTION_SHOW = 'show';
const ZENDESK_EVENT_OPEN = 'open';
const ZENDESK_EVENT_CLOSE = 'close';
const OPENING_ANIMATION_MIN_MS = 400;
const OPENING_ANIMATION_FALLBACK_MS = 2500;
const ZENDESK_CLOSE_ICON = (
  <svg
    aria-hidden="true"
    className="zendesk-widget-close-icon"
    fill="none"
    height="60"
    viewBox="0 0 60 60"
    width="60"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="30" cy="30" fill="#55406B" r="30" />
    <path
      d="M20 26L30 36L40 26"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="4"
    />
  </svg>
);

function hideZendeskLauncher() {
  getZendesk()?.(ZENDESK_MESSENGER, ZENDESK_ACTION_HIDE);
}

function getNativeLauncherFrames() {
  const launcher = document.getElementById('launcher');
  return launcher instanceof HTMLIFrameElement ? [launcher] : [];
}

function hideNativeLauncher() {
  getNativeLauncherFrames().forEach((frame) => {
    frame.style.setProperty('display', 'none', 'important');
    frame.style.setProperty('visibility', 'hidden', 'important');
    frame.style.setProperty('pointer-events', 'none', 'important');
  });
}

function isZendeskMessagingFrame(frame) {
  if (!(frame instanceof HTMLIFrameElement) || frame.id === 'launcher') {
    return false;
  }

  const rect = frame.getBoundingClientRect();
  const style = globalThis.getComputedStyle(frame);
  const zIndex = Number(style.zIndex);

  return (
    style.position === 'fixed' &&
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    zIndex >= 999999 &&
    rect.width >= 300 &&
    rect.height >= 300
  );
}

function getZendeskMessagingFrames() {
  return Array.from(document.querySelectorAll('iframe')).filter(
    isZendeskMessagingFrame,
  );
}

function positionZendeskMessagingWindow() {
  getZendeskMessagingFrames().forEach((frame) => {
    frame.dataset.zendeskMessagingWindow = 'true';
    frame.style.setProperty('bottom', '88px', 'important');
    frame.style.setProperty('right', '16px', 'important');
  });
}

function closeZendeskMessenger() {
  getZendesk()?.(ZENDESK_MESSENGER, ZENDESK_EVENT_CLOSE);
  hideZendeskLauncher();
}

function onConversationStarted() {
  applyMetadata();
}

function createChatBoxOpenHandler(stopOpeningAnimation, setIsOpen) {
  return () => {
    applyMetadata();
    setIsOpen(true);
    stopOpeningAnimation();
  };
}

function createChatBoxCloseHandler(setIsOpening, setIsOpen, openingTimeoutRef) {
  return () => {
    setIsOpening(false);
    setIsOpen(false);
    globalThis.clearTimeout(openingTimeoutRef.current);
    hideZendeskLauncher();
  };
}

function registerZendeskMessengerListeners(
  zendesk,
  stopOpeningAnimation,
  setIsOpening,
  setIsOpen,
  openingTimeoutRef,
) {
  const removeConversationStartedListener = zendesk?.(
    ZENDESK_MESSENGER_ON,
    'conversationStarted',
    onConversationStarted,
  );
  const removeChatBoxOpenListener = zendesk?.(
    ZENDESK_MESSENGER_ON,
    ZENDESK_EVENT_OPEN,
    createChatBoxOpenHandler(stopOpeningAnimation, setIsOpen),
  );
  const removeChatBoxCloseListener = zendesk?.(
    ZENDESK_MESSENGER_ON,
    ZENDESK_EVENT_CLOSE,
    createChatBoxCloseHandler(setIsOpening, setIsOpen, openingTimeoutRef),
  );

  return {
    removeConversationStartedListener,
    removeChatBoxOpenListener,
    removeChatBoxCloseListener,
  };
}

async function initializeZendeskSnippet(
  cancelled,
  stopOpeningAnimation,
  setIsOpening,
  setIsOpen,
  openingTimeoutRef,
  setSnippetReady,
) {
  await waitForZendesk();
  if (cancelled) {
    return null;
  }

  const zendesk = getZendesk();
  setSnippetReady(true);
  applyMetadata();

  zendesk?.(ZENDESK_MESSENGER, ZENDESK_ACTION_SHOW);
  hideZendeskLauncher();

  return registerZendeskMessengerListeners(
    zendesk,
    stopOpeningAnimation,
    setIsOpening,
    setIsOpen,
    openingTimeoutRef,
  );
}

function useZendeskFrameSync(snippetReady) {
  const zendeskFrameObserverRef = useRef(null);
  const zendeskFrameAnimationRef = useRef(0);

  useEffect(() => {
    const hasActiveFrameObserver = zendeskFrameObserverRef.current !== null;
    if (!snippetReady || hasActiveFrameObserver) {
      return () => {};
    }

    const scheduleZendeskFrameSync = () => {
      if (zendeskFrameAnimationRef.current) {
        return;
      }

      zendeskFrameAnimationRef.current = globalThis.requestAnimationFrame(() => {
        zendeskFrameAnimationRef.current = 0;
        hideNativeLauncher();
        positionZendeskMessagingWindow();
      });
    };

    // Create one observer for the snippet lifecycle; cleanup resets the ref.
    zendeskFrameObserverRef.current = new MutationObserver(
      scheduleZendeskFrameSync,
    );
    zendeskFrameObserverRef.current.observe(document.body, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      childList: true,
      subtree: true,
    });
    scheduleZendeskFrameSync();

    return () => {
      zendeskFrameObserverRef.current?.disconnect();
      zendeskFrameObserverRef.current = null;
      globalThis.cancelAnimationFrame(zendeskFrameAnimationRef.current);
      zendeskFrameAnimationRef.current = 0;
    };
  }, [snippetReady]);
}

const ZendeskWidget = () => {
  const { config: countryConfig } = useCountryConfig();
  const {
    state: { countryResolved },
  } = useContext(Context);
  const [snippetReady, setSnippetReady] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const openingTimeoutRef = useRef();
  const openingStartedAtRef = useRef(0);

  const zendeskKey = countryConfig?.zendeskKey;

  const stopOpeningAnimation = () => {
    const elapsed = Date.now() - openingStartedAtRef.current;
    const remaining = Math.max(0, OPENING_ANIMATION_MIN_MS - elapsed);
    globalThis.clearTimeout(openingTimeoutRef.current);
    openingTimeoutRef.current = globalThis.setTimeout(() => {
      setIsOpening(false);
      hideNativeLauncher();
    }, remaining);
  };

  useEffect(() => {
    if (!countryResolved) {
      return undefined;
    }

    document.getElementById(ZE_SNIPPET_ID)?.remove();

    if (!zendeskKey) {
      return undefined;
    }

    const script = document.createElement('script');
    script.id = ZE_SNIPPET_ID;
    script.async = true;
    script.src = `${ZENDESK_SNIPPET_BASE_URL}?key=${zendeskKey}`;

    let removeChatBoxOpenListener;
    let removeChatBoxCloseListener;
    let removeConversationStartedListener;
    let cancelled = false;

    const handleSnippetLoad = () => {
      void initializeZendeskSnippet(
        cancelled,
        stopOpeningAnimation,
        setIsOpening,
        setIsOpen,
        openingTimeoutRef,
        setSnippetReady,
      )
        .then((listeners) => {
          if (!listeners) return;
          removeConversationStartedListener =
            listeners.removeConversationStartedListener;
          removeChatBoxOpenListener = listeners.removeChatBoxOpenListener;
          removeChatBoxCloseListener = listeners.removeChatBoxCloseListener;
        })
        .catch((e) => {
          console.error('Zendesk snippet init failed', e);
        });
    };

    script.onload = handleSnippetLoad;
    document.head.appendChild(script);

    return () => {
      cancelled = true;
      script.onload = null;
      removeChatBoxOpenListener?.();
      removeChatBoxCloseListener?.();
      removeConversationStartedListener?.();
      globalThis.clearTimeout(openingTimeoutRef.current);
      document.getElementById(ZE_SNIPPET_ID)?.remove();
      setSnippetReady(false);
      setIsOpening(false);
      setIsOpen(false);
    };
  }, [countryResolved, zendeskKey]);

  useZendeskFrameSync(snippetReady);

  const handleLauncherClick = () => {
    const zendesk = getZendesk();
    if (!snippetReady || zendesk === undefined) {
      return;
    }

    if (isOpen) {
      setIsOpening(false);
      globalThis.clearTimeout(openingTimeoutRef.current);
      setIsOpen(false);
      closeZendeskMessenger();
      hideNativeLauncher();
      return;
    }

    setIsOpening(true);
    openingStartedAtRef.current = Date.now();
    globalThis.clearTimeout(openingTimeoutRef.current);
    openingTimeoutRef.current = globalThis.setTimeout(() => {
      setIsOpening(false);
      hideNativeLauncher();
      positionZendeskMessagingWindow();
    }, OPENING_ANIMATION_FALLBACK_MS);

    const openMessenger = () => {
      applyMetadata(() => {
        zendesk(ZENDESK_MESSENGER, ZENDESK_ACTION_SHOW);
        zendesk(ZENDESK_MESSENGER, ZENDESK_EVENT_OPEN);
        positionZendeskMessagingWindow();
      });
    };

    const pathname = globalThis.location?.pathname ?? '/';
    syncPortal(pathname, openMessenger);
    hideNativeLauncher();
    positionZendeskMessagingWindow();
  };

  if (!countryResolved || !snippetReady) {
    return null;
  }

  return (
    <button
      aria-label={isOpen ? 'Close support chat' : 'Open support chat'}
      className={`zendesk-widget-launcher ${isOpening ? 'is-opening' : ''} ${
        isOpen ? 'is-open' : ''
      }`}
      onClick={handleLauncherClick}
      type="button"
    >
      {isOpen ? (
        ZENDESK_CLOSE_ICON
      ) : (
        <img
          alt=""
          className="zendesk-widget-launcher-image"
          src={zendeskCustomLauncher}
        />
      )}
    </button>
  );
};

export default ZendeskWidget;
